import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { ManagementEntity } from '../../types';

type FieldType = 'text' | 'number' | 'textarea' | 'checkbox' | 'date';

export interface EntityField {
  key: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  placeholder?: string;
}

interface Props<T extends ManagementEntity> {
  title: string;
  items: T[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
  fields: EntityField[];
  fetchAll: () => any;
  createItem: (data: Partial<T>) => any;
  updateItem: (data: { id: number; data: Partial<T> }) => any;
  deleteItem: (id: number) => any;
  canCreate?: boolean;
  canEdit?: boolean;
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: 'var(--crm-surface)',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: 'var(--crm-shadow-sm)',
};

function EntityManagementPage<T extends ManagementEntity>({
  title,
  items,
  loading,
  submitting,
  error,
  fields,
  fetchAll,
  createItem,
  updateItem,
  deleteItem,
  canCreate = true,
  canEdit = true,
}: Props<T>) {
  const dispatch = useAppDispatch();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const initialForm = useMemo(
    () =>
      fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.key]: field.type === 'checkbox' ? false : '',
        }),
        {} as Record<string, string | number | boolean>
      ),
    [fields]
  );
  const [form, setForm] = useState<Record<string, string | number | boolean>>(initialForm);

  useEffect(() => {
    dispatch(fetchAll());
  }, [dispatch, fetchAll]);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const handleChange = (field: EntityField, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field.key]: value }));
  };

  const validate = () => {
    for (const field of fields) {
      if (!field.required) continue;
      const value = form[field.key];
      if (field.type === 'checkbox') continue;
      if (value === '' || value === null || value === undefined) {
        return `Поле "${field.label}" є обов'язковим`;
      }
    }
    return null;
  };

  const toPayload = () =>
    fields.reduce(
      (acc, field) => {
        const value = form[field.key];
        if (field.type === 'number') {
          if (value === '') return acc;
          return { ...acc, [field.key]: Number(value) };
        }
        return { ...acc, [field.key]: value };
      },
      {} as Partial<T>
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || (!editingId && !canCreate)) return;
    const formError = validate();
    setValidationError(formError);
    if (formError) return;

    const payload = toPayload();
    if (editingId) {
      await dispatch(updateItem({ id: editingId, data: payload }));
    } else {
      await dispatch(createItem(payload));
    }
    setEditingId(null);
    setForm(initialForm);
  };

  const handleEdit = (item: T) => {
    const itemValues = item as Record<string, unknown>;
    setEditingId(item.id);
    setValidationError(null);
    setForm(
      fields.reduce(
        (acc, field) => {
          const rawValue = itemValues[field.key];
          const value =
            field.type === 'checkbox'
              ? Boolean(rawValue)
              : field.type === 'number'
                ? typeof rawValue === 'number'
                  ? rawValue
                  : rawValue === null || rawValue === undefined || rawValue === ''
                    ? ''
                    : Number(rawValue)
                : String(rawValue ?? '');
          return {
            ...acc,
            [field.key]: value,
          };
        },
        {} as Record<string, string | number | boolean>
      )
    );
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Ви впевнені, що хочете видалити запис?')) return;
    await dispatch(deleteItem(id));
  };

  return (
    <div className="entity-management-page" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>{title}</h2>
      {(canEdit && (canCreate || editingId !== null)) && <form
        className="entity-management-form"
        onSubmit={handleSubmit}
        style={{
          marginTop: '16px',
          padding: '16px',
          background: 'var(--crm-surface)',
          color: 'var(--crm-text)',
          border: '1px solid var(--crm-border)',
          borderRadius: '8px',
          boxShadow: 'var(--crm-shadow-sm)',
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}
      >
        {fields.map((field) => (
          <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: 'var(--crm-text)' }}>
            {field.label}
            {field.type === 'textarea' ? (
              <textarea
                value={String(form[field.key] ?? '')}
                onChange={(e) => handleChange(field, e.target.value)}
                placeholder={field.placeholder}
                style={{ border: '1px solid var(--crm-border)', borderRadius: '6px', padding: '8px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
              />
            ) : field.type === 'checkbox' ? (
              <input
                type="checkbox"
                checked={Boolean(form[field.key])}
                onChange={(e) => handleChange(field, e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
            ) : (
              <input
                type={field.type || 'text'}
                value={String(form[field.key] ?? '')}
                onChange={(e) => handleChange(field, e.target.value)}
                placeholder={field.placeholder}
                min={field.type === 'number' ? 0 : undefined}
                style={{ border: '1px solid var(--crm-border)', borderRadius: '6px', padding: '8px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
              />
            )}
          </label>
        ))}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              background: 'var(--crm-success-bg)',
              color: 'var(--crm-success)',
              border: '1px solid var(--crm-success)',
              borderRadius: '6px',
              padding: '10px 14px',
              cursor: 'pointer',
            }}
          >
            {editingId ? 'Зберегти' : 'Створити'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(initialForm);
                setValidationError(null);
              }}
              style={{
                background: 'var(--crm-surface-alt)',
                color: 'var(--crm-text)',
                border: '1px solid var(--crm-border)',
                borderRadius: '6px',
                padding: '10px 14px',
                cursor: 'pointer',
              }}
            >
              Скасувати
            </button>
          )}
        </div>
      </form>}

      {(error || validationError) && (
        <div style={{ marginTop: '12px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: '10px', borderRadius: '6px' }}>
          {validationError || error}
        </div>
      )}

      {loading ? (
        <p style={{ marginTop: '20px' }}>Завантаження...</p>
      ) : (
        <div className="entity-management-table-scroll crm-mobile-table-scroll">
        <table className="crm-table" style={{ ...tableStyle, marginTop: '20px' }}>
          <thead style={{ background: 'var(--crm-navbar-dropdown-bg)', color: 'var(--crm-primary-contrast)' }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
              {fields.map((field) => (
                <th key={field.key} style={{ padding: '12px', textAlign: 'left' }}>
                  {field.label}
                </th>
              ))}
              <th style={{ padding: '12px', textAlign: 'left' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.id} style={{ background: index % 2 === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-surface)' }}>
                <td style={{ padding: '10px 12px' }}>{item.id}</td>
                {fields.map((field) => {
                  const value = (item as Record<string, unknown>)[field.key];
                  return (
                    <td key={field.key} style={{ padding: '10px 12px' }}>
                      {typeof value === 'boolean' ? (value ? 'Так' : 'Ні') : String(value ?? '—')}
                    </td>
                  );
                })}
                <td style={{ padding: '10px 12px', display: 'flex', gap: '8px' }}>
                  {canEdit && <button
                    onClick={() => handleEdit(item)}
                    style={{
                      background: 'var(--crm-info-bg)',
                      color: 'var(--crm-info)',
                      border: '1px solid var(--crm-info)',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    Редагувати
                  </button>}
                  {canEdit && <button
                    onClick={() => handleDelete(item.id)}
                    style={{
                      background: 'var(--crm-danger-bg)',
                      color: 'var(--crm-danger)',
                      border: '1px solid var(--crm-danger)',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    Видалити
                  </button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={fields.length + 2} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                  Дані відсутні
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export default EntityManagementPage;
